import { reactive } from 'vue';

import { useObjectSubset } from '../common/composables/useObjectSubset';
import { AllowedInterfaces } from './models/interfaces/AllowedInterfaces';
import { ValidEntryPoints } from './models/types/validEntryPoints';
import HelpCenterDefaults from './models/pageDefaults/HelpCenterDefaults';
import HelpCenterComponentDefaults from './models/pageDefaults/HelpCenterComponentDefaults';
import { MemberNameChangeDefaults } from './models/pageDefaults/MemberNameChangeDefaults';
import EmailFormDefaults from './models/pageDefaults/EmailFormDefaults';
import PriceAdjustmentDefaults from '../pages/priceAdjustment/models/constants/PriceAdjustmentDefaults';
import OrderHelpDefaults from './models/pageDefaults/OrderHelpDefaults';

export interface StoreProps<T> {
  globalState: T;
  getValues: <K extends keyof T>(keys: K[]) => Pick<T, K>;
  setValues: (payload: Partial<T>) => T;
}

const defaultModels = {
  'home': HelpCenterDefaults,
  'helpCenters': HelpCenterComponentDefaults,
  'emailForm': EmailFormDefaults,
  'memberNameChange': MemberNameChangeDefaults,
  'price-adjustment': PriceAdjustmentDefaults,
  'orderHelp': OrderHelpDefaults,
}
let globalState: AllowedInterfaces = reactive(<AllowedInterfaces>{});

function deepClone(
  props: Partial<AllowedInterfaces>,
  model?: any
) {
  if (null === props || "object" !== typeof props) return props;
  if (props instanceof Date) {
    const copy = new Date();
    copy.setTime(props.getTime());
    return copy;
  }
  if (props instanceof Array) {
    const copy = [];
    for (let i = 0, len = props.length; i < len; i++) {
      copy[i] = deepClone(props[i]);
    }
    return copy;
  }
  if (props instanceof Object) {
    const copy = {};
    for (const attr in props) {
      if (props.hasOwnProperty(attr)) {
        if (props[attr] === null) {
          if (model) copy[attr] = model[attr]
        }
        else {
          copy[attr] = deepClone(props[attr])
        }
      }
    }
    return copy;
  }
  throw new Error("Unable to copy obj this object.");
}

function initializeGlobalSate(props: Partial<AllowedInterfaces>, entry: ValidEntryPoints) {
  const model = defaultModels[entry];
  Object.keys(model).forEach((key) => {
    if (!props.hasOwnProperty(key)) {
      props[key] = null
    }
  });
  globalState = deepClone(props, model) as AllowedInterfaces;
  Object.seal(globalState);
}

export const useDataStore = (props: AllowedInterfaces, entry: ValidEntryPoints = 'home') => {
  if (!Object.entries(globalState).length) {
    initializeGlobalSate(props, entry);
  }

  const getValues = <T extends keyof AllowedInterfaces>(payload: T[]) => useObjectSubset('subsetObjectByKeys', globalState, payload);

  const setValues = <T extends keyof AllowedInterfaces>(payload: T[]) => {
    globalState = {
      ...globalState,
      ...payload,
    };
    return globalState
  }

  return {
    getValues,
    setValues,
  };
};